/**
 * The quote card — the message a reply answers, as a brand-barred one-line excerpt.
 *
 * Drawn in two places from one component so they cannot drift: inside the composer while
 * the reply is written ("Reply to <author>", with a dismiss button), and at the top of the
 * posted reply's bubble (the author alone; a click jumps to the original).
 *
 * The clickable card is a `role="button"` box, not a `<button>`: the excerpt is rendered
 * markdown and may hold links and mentions, and interactive content inside a `<button>` is
 * invalid HTML. Those links are inert here (`pointer-events-none`) — the excerpt is a
 * preview of the original, so a click always means "go to the original".
 */

import { cn } from "cn";
import { useUserDetail } from "@/api/use-user-detail";
import { Button } from "@/components/ui/button/button";
import { BwCloseLine } from "@/components/ui/icons";
import { Markdown } from "@/components/ui/markdown";
import type { ChannelQuotedMessage } from "../_api/channel-endpoints";

type ChannelQuoteCardProps = {
  quote: ChannelQuotedMessage;
  /** Words before the author's name — "Reply to" in the composer, nothing in a bubble. */
  titlePrefix?: string;
  /** Jump to the quoted message. Omitted, the card is not interactive. */
  onOpen?: () => void;
  /** Drop the quote. Omitted, no dismiss button. */
  onDismiss?: () => void;
  className?: string;
};

export function ChannelQuoteCard({
  quote,
  titlePrefix,
  onOpen,
  onDismiss,
  className,
}: ChannelQuoteCardProps) {
  const { data: author } = useUserDetail(quote.userCreatedId, {
    enabled: !!quote.userCreatedId,
  });
  const authorName = author?.firstName || author?.name || "";
  const title = [titlePrefix, authorName].filter(Boolean).join(" ");

  const excerpt = (
    <div className="flex grow items-stretch gap-2 min-w-0">
      <span className="w-1 shrink-0 bg-bg-primary rounded-full" />
      <div className="flex flex-col min-w-0 text-left">
        <p className="text-sm font-medium text-text-primary truncate">
          {title}
        </p>
        {quote.body ? (
          <Markdown
            content={quote.body}
            className={cn(
              /* sorted */
              "line-clamp-1",
              "text-xs text-text-dark-high",
              "[&_*]:inline",
              "[&_a]:pointer-events-none",
            )}
          />
        ) : (
          <p className="text-xs text-text-dark-high">Attachment</p>
        )}
      </div>
    </div>
  );

  return (
    <div
      data-test="quoted-message"
      className={cn(
        /* sorted */
        "flex",
        "items-center gap-2",
        "w-full",
        "p-2",
        "bg-bg-dark-min rounded-lg",
        className,
      )}
    >
      {onOpen ? (
        <div
          role="button"
          tabIndex={0}
          aria-label={
            authorName
              ? `Go to ${authorName}'s message`
              : "Go to the quoted message"
          }
          onClick={onOpen}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            onOpen();
          }}
          className="flex grow min-w-0 cursor-pointer"
        >
          {excerpt}
        </div>
      ) : (
        excerpt
      )}
      {onDismiss && (
        <Button
          type="button"
          kind="ghost"
          variant="neutral"
          size="icon-xs"
          aria-label="Cancel reply"
          onClick={onDismiss}
        >
          <BwCloseLine />
        </Button>
      )}
    </div>
  );
}
